import _ from 'lodash';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useContext, useState } from 'react';
import { DialogContext } from '../../../../../store/context/DialogContext';
import { useTranslation } from '../../../../../store/context/TranslationContext';
import ButtonGroupNew from '../../BusinessLiteForms/components/ButtonGroupNew';
import { createWrapperFieldRenderer } from '../../helpers/createWrapperFieldRenderer';
import { STEP_NUM } from '../../IPlushelper';
import { MIN_USERNAME_LENGTH } from '../../../components/userName/constants';
import { buildUserDetailsFieldOrder, handleValidation, hasValue, validatePhoneNumber, validateSSN, validateUserDetailsField } from './helper';
import { shouldShowField } from '../../../../../components/InputField/helper';
import { buildAutomationChildTestId } from '../../../../../components/InputField/testId';

export { handleValidation } from './helper';

const USER_DETAILS_TEST_ID = "individual-plus-user-details";


const UserDetailsForm = ({
    handleChangeViaObject,
    onOtpVerify,
    hideButtons,
    asLabel,
    data,
    handleUpdate,
    hasError,
    setHasError,
    setErrorMessage = () => { },
    loading,
    setLoading,
    cyclosFields,
    // Broker-managed accounts (Q3) are created without a username — hide + skip the
    // cyclosUserName field in broker mode. Default false ⇒ direct flow unchanged.
    isBrokerMode = false,
}) => {
    const [isValidSSN, setIsValidSSN] = useState(true);
    const [isValidUsername, setIsValidUsername] = useState();
    const { showError } = useContext(DialogContext);
    const [centralErrors, setCentralErrors] = useState({});
    const [shouldPerformOtpVerification, setShouldPerformOtpVerification] = useState(false);

    const { t, } = useTranslation();

    const getWrapperValidationError = useCallback(({
        jsonField,
        value,
        dataObj: currentData,
    }) => validateUserDetailsField(jsonField, value, currentData), []);

    const renderWrapperField = createWrapperFieldRenderer({
        fieldMap: cyclosFields,
        dataObj: data,
        disabled: asLabel,
        showError: hasError,
        defaultOnChange: handleChangeViaObject,
        defaultCustomValidation: getWrapperValidationError,
    });

    const handleSubmit = () => {
        const shouldRequirePinCode = shouldShowField(data, cyclosFields?.['address.pinCode']?.showIf);
        const shouldRequireSSN = shouldShowField(data, cyclosFields?.ssn?.showIf);
        const shouldRequireTaxId = shouldShowField(data, cyclosFields?.taxId?.showIf);
        const orderedFields = buildUserDetailsFieldOrder({
            includePinCode: shouldRequirePinCode,
            includeUsername: !isBrokerMode,
            includeTaxId: shouldRequireTaxId,
            includeSSN: shouldRequireSSN,
            streetBeforeBuilding: true,
        });
        const errors = [];

        orderedFields.forEach(field => {
            const fieldValue = _.get(data, field);
            if (field === 'mobilePhone' && typeof centralErrors?.mobilePhone === 'string' && centralErrors.mobilePhone.trim()) {
                errors.push(centralErrors.mobilePhone);
            } else if (field === 'mobilePhone' && !validatePhoneNumber(fieldValue)) {
                errors.push(`Please enter a valid Mobile Phone`);
            } else if (field === 'mobilePhone' && !isBrokerMode && (shouldPerformOtpVerification ? !data?.phoneVerifiedViaOTP : false)) {
                errors.push(t("Please verify mobile phone with OTP"));
            } else if (field === 'ssn' && hasValue(fieldValue) && !validateSSN(fieldValue)) {
                errors.push(`Please enter a valid SSN number.`);
            } else if (field === 'cyclosUserName' && hasValue(fieldValue) && String(fieldValue).trim().length < MIN_USERNAME_LENGTH) {
                errors.push(`Username must be at least ${MIN_USERNAME_LENGTH} characters long.`);
            } else if (field === 'cyclosUserName' && hasValue(fieldValue) && isValidUsername === false) {
                errors.push("Username not available");
            } else if (field === 'cyclosUserName' && hasValue(fieldValue) && isValidUsername !== true) {
                errors.push("Please enter a valid Username.");
            } else {
                const fieldError = validateUserDetailsField(cyclosFields?.[field], fieldValue, data)
                    || handleValidation(field, fieldValue, cyclosFields?.[field]);
                if (fieldError) errors.push(fieldError);
            }
        });

        if (shouldRequireSSN && hasValue(data?.ssn) && !isValidSSN) {
            errors.push(t(handleValidation('ssn', data?.ssn, cyclosFields?.ssn)));
        }

        if (errors.length > 0) {
            setHasError(true);
            setErrorMessage(errors.join(' '));
            showError((errors[0]));
            return;
        }

        setHasError(false);
        return handleUpdate({ nextStep: STEP_NUM.DECLARATION_FORM });
    };

    return (
        <div className="row" data-testid={USER_DETAILS_TEST_ID}>
            <InputLabel label={t("Name")} required testId={buildAutomationChildTestId(USER_DETAILS_TEST_ID, "name-section-label")} />
            <p
                className='ps-3 ms-0 mb-2'
                style={{ fontSize: 11, fontStyle: 'italic' }}
                data-testid={buildAutomationChildTestId(USER_DETAILS_TEST_ID, "name-section-hint")}
            >
                Please enter your name exactly as it appears on your passport.
            </p>
            {['firstName', 'middleName', 'lastName'].map(field => (
                renderWrapperField(field, { className: "col-sm-4 mt-0" })
            ))}

            <InputLabel label={t("Address")} required testId={buildAutomationChildTestId(USER_DETAILS_TEST_ID, "address-section-label")} />

            {renderWrapperField('address.street')}


            {[ /*'street',/*'pinCode',*/ "buildingNumber"].map(field => (
                renderWrapperField(`address.${field}`, { className: "col-12" })
            ))}



            {['city', 'state'].map(field => (
                renderWrapperField(`address.${field}`, { className: "col-12 col-sm-3" })
            ))}

            {shouldShowField(data, cyclosFields?.['address.pinCode']?.showIf)
                && renderWrapperField('address.pinCode', { className: "col-12 col-sm-3 me-0 pe-0" })}


            {renderWrapperField('address.country', { className: "col-12 col-sm-3 with-margin-top" })}

            {!shouldShowField(data, cyclosFields?.['address.pinCode']?.showIf)
                && <div className="col-12 col-sm-3 me-0 pe-0" aria-hidden="true" />}

            {renderWrapperField('mobilePhone', {
                className: "mt-2",
                customOperator: setShouldPerformOtpVerification,
                customProp: onOtpVerify,
                onError: (v) => {
                    setCentralErrors((prevErrors) => ({
                        ...prevErrors,
                        mobilePhone: v,
                    }));
                },
            })}

            {renderWrapperField('email', { className: "mt-4", disabled: true })}

            {!isBrokerMode && renderWrapperField('cyclosUserName', {
                className: "col-sm-12 mt-0",
                customOperator: setIsValidUsername,
                customProp: isValidUsername,
                loading,
                setLoading,
            })}

            {renderWrapperField('gender')}


            {renderWrapperField('nationality', { className: "col-12 mt-3" })}

            {renderWrapperField('dob', { className: "col-sm-12 col-12 pt-2 pb-1" })}
            {renderWrapperField('taxId', { className: "col-12" })}

            {renderWrapperField('ssn', {
                className: "col-12 mt-2",
                customOperator: setIsValidSSN,
                customProp: {
                    notifyParent: setIsValidSSN,
                },
            })}

            {renderWrapperField('Mother_Maiden_Name', { className: "col-12 mt-3" })}

            {!hideButtons && (
                <div className='mt-4' data-testid={buildAutomationChildTestId(USER_DETAILS_TEST_ID, "footer")}>
                    <ButtonGroupNew
                        onNextClick={handleSubmit}
                        step={1}
                        showPageNum={false}
                        pageNum={1}
                        hideBackButton
                        isDisabled={asLabel}
                        asLabel={false}
                        loading={loading}
                        testId={buildAutomationChildTestId(USER_DETAILS_TEST_ID, "navigation")}
                    />
                </div>
            )}
        </div>
    );
};

export const InputLabel = ({ label, required, testId }) => (
    <div
        style={{ minWidth: "120px", fontSize: 16, margin: "5px 0px 0px 5px", fontWeight: "500" }}
        data-testid={testId}
    >
        {label}
        {required && <font color="red">*</font>}
    </div>
);

export default observer(UserDetailsForm);
